/**
 * What the table blocks share: the sentiment badge map, the whole-number format
 * and the status-aware empty line. Ported from the prototype's
 * `sentimentBadge.ts`. The pagination window is `Pagination`'s own default now.
 */
import type { BadgeType } from '../../../atoms/Badge/Badge';
import type { WidgetStatus } from '../../widgets/shared/cardShell';

/**
 * A sentiment value → the `Badge` type that colours it. `gray` is the fallback
 * at every call site, not a fourth entry: a value outside the three is not a
 * sentiment.
 */
export const SENTIMENT_BADGE: Readonly<Record<string, BadgeType>> = {
  positive: 'positive',
  neutral: 'neutral',
  negative: 'negative',
};

/** The format every table cell's count uses: 999, 13.8K, 1.2M. */
export const countFormat = new Intl.NumberFormat('en-US', {
  notation: 'compact',
  maximumFractionDigits: 1,
});

/**
 * A table's empty line, status-aware. `Table` has no `State=Loading` variant
 * (the prototype's tables re-derived instantly), so while the data is on its
 * way the body says so in the empty band — the same thing Community's ranking
 * tables did while the prototype dashboard settled. `error` / `notConnected`
 * have no frame yet (docs/design/open-questions.md #2).
 */
export function tableEmptyLabel(status: WidgetStatus, readyLabel: string): string {
  switch (status) {
    case 'loading':
      return 'Loading…';
    case 'error':
      return 'Couldn’t load this table.';
    case 'notConnected':
      return 'Live data for this table is not connected yet.';
    default:
      return readyLabel;
  }
}
